/**
 * Today — the screen that answers "what is going on".
 * FR-401..FR-406, FR-412, L-09.
 *
 * ## This screen is the regulatory boundary
 *
 * Everything Thana could be accused of claiming, it claims here. The rules it
 * holds, all from docs/12 Section 4 and the legal map Section 4:
 *
 *  - Below two completed cycles: NO estimate, and an explanation. A
 *    confident-looking wrong date on day one is worse than an honest blank
 *    (FR-404).
 *  - Confidence is stated as a reason, never as a label or a percentage. The
 *    model has no calibrated probability, and implying one would be a quiet lie
 *    (FR-403).
 *  - The estimated fertile window appears only at medium or high confidence, and
 *    never without the warning under it (FR-405).
 *  - The disclaimer is on this screen, always, not one tap away (L-09).
 *
 * The exercise engine joins this screen in v0.6.
 */
import { en, t } from '@copy/index'
import { useEntries, usePrediction, useSettings } from '@contexts/index'
import { Notice } from '@shared/ui'

const readable = (date: string) => t.date(new Date(`${date}T12:00:00`))

/**
 * The sentence explaining what the estimate rests on.
 *
 * Three shapes rather than one, because the honest wording differs: a spread of
 * zero is a real result and "varied by about 0 days" reads like a bug, and a
 * spread of one day should say "a day" rather than "1 days".
 */
function basisText(basedOn: number, variation: number, rough: boolean): string {
  if (variation === 0) return t('prediction.basisExact', { count: basedOn })
  const forms = rough ? en.prediction.basisRough : en.prediction.basis
  return t.plural(forms, variation, { count: basedOn, variation })
}

export function TodayPage() {
  const { derived } = useEntries()
  const { settings, suspendPredictions } = useSettings()
  const prediction = usePrediction()

  const suspended = settings.predictions.suspended

  return (
    <main className="flex flex-col gap-6 p-4">
      <section>
        {derived.currentCycle ? (
          <h1 className="text-xl font-medium">
            {t('prediction.cycleDay', { day: derived.currentCycle.dayOfCycle })}
          </h1>
        ) : (
          <p className="text-sm text-ink-muted">{t('prediction.noCycleYet')}</p>
        )}
      </section>

      {prediction.kind === 'none' ? (
        <section>
          <p className="text-sm text-ink-muted">
            {prediction.reason === 'suspended'
              ? t('prediction.none.suspended')
              : t('prediction.none.insufficientHistory')}
          </p>
        </section>
      ) : (
        <>
          <section>
            <p className="text-base">
              {t('prediction.estimate', { date: readable(prediction.predictedStart) })}
            </p>
            {/* The reason, not a label. A user who can see what the estimate
                rests on can judge it; a badge reading "medium" tells them
                nothing they can act on. */}
            <p className="pt-1 text-sm text-ink-muted">
              {basisText(
                prediction.basedOn,
                prediction.variation,
                prediction.confidence === 'low',
              )}
            </p>
          </section>

          {prediction.fertileWindow ? (
            <section>
              <h2 className="text-sm font-medium">{t('prediction.fertile.heading')}</h2>
              <p className="pt-1 text-sm">
                {t('prediction.fertile.range', {
                  from: readable(prediction.fertileWindow.from),
                  to: readable(prediction.fertileWindow.to),
                })}
              </p>
              <p className="pt-1 text-sm text-ink-muted">
                {t('prediction.fertile.ovulation', {
                  date: readable(prediction.fertileWindow.ovulation),
                })}
              </p>
              <p className="pt-1 text-xs text-ink-muted">{t('prediction.fertile.basis')}</p>
              <div className="pt-2">
                {/* Never optional, never collapsed, never below the fold
                    relative to the window itself. FR-406. */}
                <Notice tone="warning">{t('prediction.fertile.warning')}</Notice>
              </div>
            </section>
          ) : null}
        </>
      )}

      <section className="border-t border-line pt-4">
        <h2 className="text-sm font-medium">{t('prediction.suspend.heading')}</h2>
        <p className="pt-1 text-xs text-ink-muted">{t('prediction.suspend.explain')}</p>
        {/* The current state is stated in text, and the button is labelled with
            the ACTION it performs. An earlier version put `aria-pressed` on the
            button as well, which contradicted itself: pressed=true while the
            label read "Turn estimates on". State and action are two different
            things and they get two different elements. */}
        <p className="pt-2 text-sm" role="status">
          {suspended ? t('prediction.suspend.off') : t('prediction.suspend.on')}
        </p>
        <button
          type="button"
          onClick={() => void suspendPredictions(!suspended)}
          className="mt-2 min-h-11 w-full rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {suspended ? t('prediction.suspend.turnOn') : t('prediction.suspend.turnOff')}
        </button>
      </section>

      {/* L-09. Present on every render of this screen, including when there is
          no estimate to qualify — the "not a medical device" half applies
          regardless. */}
      <p className="border-t border-line pt-4 text-xs text-ink-muted">
        {t('disclaimer.full')}
      </p>
    </main>
  )
}
